/**
 * Pure derivations for the Today screen's work-order groups and the
 * state-change detection that drives their motion.
 *
 * The screen splits live work orders into "your jobs" (dispatched to me or
 * taken by me), "open jobs" (agreed work nobody holds — the floor pool,
 * there to be taken), and everything else (a colleague's job I share a
 * step with). The whole CARD moves — a service is never lifted out of its
 * work order, so nothing is duplicated and a job always reads as a unit.
 *
 * Motion needs to know what CHANGED, not what IS: a background refetch
 * lands every 30s (App.tsx `refetchInterval`), and animating off current
 * state alone would replay the green fill on every one of those. So the
 * route keeps the previous snapshot in a ref and diffs it here. A card
 * that moves between groups unmounts and remounts (different parent),
 * which is why the diff lives at route level rather than inside the card —
 * the card can't remember what it was.
 */
import type { DoListService, DoListServiceState, DoListWorkOrder } from '@/apis/types'

/**
 * The floor pool: an agreed service nobody has taken yet.
 *
 * The pool is made of SERVICES, not tasks — "Auftrag übernehmen" takes every
 * one of them on the job. A service the customer has not agreed to
 * (PENDING_APPROVAL) is not on offer, and one already in someone's hands is
 * not either.
 */
export function isLooseService(service: DoListService): boolean {
  return service.status === 'OPEN' && service.technicianId === null
}

/**
 * The technician already holds a stake in this job — a service of theirs,
 * or the job itself, dispatched to them as a whole. Once true, remaining
 * loose work on the job is taken service by service ("Take service") rather
 * than by re-claiming the whole job — see `isClaimable`.
 */
export function hasStake(wo: DoListWorkOrder, meId: string | null = null): boolean {
  return isAssignedToMe(wo, meId) || wo.services.some((s) => s.mine)
}

/**
 * Something on this job is there to be taken AS THE WHOLE JOB.
 *
 * False once the technician already has a stake in it: from there, loose
 * work belongs to the per-service Take, not a second whole-job claim — see
 * `hasStake`.
 */
export function isClaimable(wo: DoListWorkOrder, meId: string | null = null): boolean {
  return !hasStake(wo, meId) && wo.services.some(isLooseService)
}

/** Services that count toward done/total — awaiting approval counts in neither. */
export function countableServices(wo: DoListWorkOrder): DoListService[] {
  return wo.services.filter((s) => s.status !== 'PENDING_APPROVAL')
}

/** The service carries its own state now — it used to be read off the one
 *  step underneath it, which was a copy of the service by construction. */
export function isServiceDone(service: DoListService): boolean {
  return service.state === 'done'
}

/** Every service done → the WO drops to the DONE TODAY receipts. */
export function isDoneToday(wo: DoListWorkOrder): boolean {
  const rows = countableServices(wo)
  return rows.length > 0 && rows.every(isServiceDone)
}

export function hasMyRunning(wo: DoListWorkOrder): boolean {
  return wo.services.some((s) => s.state === 'in_progress' && s.mine)
}

export function hasMyPaused(wo: DoListWorkOrder): boolean {
  return wo.services.some((s) => s.state === 'paused' && s.mine)
}

/**
 * Work of mine that nobody has started yet.
 *
 * This is the NORMAL state the moment a manager dispatches a job to me or I
 * take one off the floor: the service is mine and not a single timer has
 * run.
 *
 * A PENDING_APPROVAL service is deliberately excluded. It is mine on paper
 * and unworkable in fact, so putting it in "your jobs" would promise
 * something the customer has not yet agreed to. So is a finished one — it
 * is done, not in flight.
 *
 * <p>There used to be a second clause here for a per-task override on
 * somebody else's service. Overrides are gone with the steps, so a service
 * being mine is now the only way work can be.
 */
export function hasMyAssigned(wo: DoListWorkOrder): boolean {
  return wo.services.some((s) => s.mine && s.status === 'OPEN' && s.state !== 'done')
}

/**
 * The job was dispatched to me as a whole. Normally every service says so
 * too (intake and the board hand them over), but a split job can name me
 * as its owner while colleagues hold the work — it is still mine to open
 * and to hand back, so it belongs under "your jobs".
 */
export function isAssignedToMe(wo: DoListWorkOrder, meId: string | null): boolean {
  return meId !== null && wo.assignedTechnicianId === meId
}

/** my running card first, then my paused, then work of mine not yet started. */
export function cardRank(wo: DoListWorkOrder): number {
  if (hasMyRunning(wo)) return 0
  if (hasMyPaused(wo)) return 1
  if (hasMyAssigned(wo)) return 2
  return 3
}

/** A WO belongs to "your jobs" while it holds live work of mine — a running
 *  or paused timer, a service dispatched to or taken by me that hasn't
 *  started — or was dispatched to me as a whole. */
export function isMine(wo: DoListWorkOrder, meId: string | null = null): boolean {
  return hasMyRunning(wo) || hasMyPaused(wo) || hasMyAssigned(wo) || isAssignedToMe(wo, meId)
}

/** The signed-in technician may open this job: they hold a service on it or
 *  own it — the same test the backend's access guard applies, and exactly
 *  what {@link hasStake} means. */
export function canOpen(wo: DoListWorkOrder, meId: string | null = null): boolean {
  return hasStake(wo, meId)
}

export interface TodayGroups {
  /** Cards holding my work, running first. */
  readonly mine: DoListWorkOrder[]
  /** Agreed work nobody holds — there to be taken. */
  readonly pool: DoListWorkOrder[]
  /** Everything else still open (a colleague's job I share a step with). */
  readonly rest: DoListWorkOrder[]
  /** Fully-done WOs, rendered as receipts. */
  readonly done: DoListWorkOrder[]
}

export function groupWorkOrders(
  workOrders: readonly DoListWorkOrder[],
  meId: string | null = null,
): TodayGroups {
  const done: DoListWorkOrder[] = []
  const mine: DoListWorkOrder[] = []
  const pool: DoListWorkOrder[] = []
  const rest: DoListWorkOrder[] = []

  for (const wo of workOrders) {
    if (isDoneToday(wo)) done.push(wo)
    else if (isMine(wo, meId)) mine.push(wo)
    else if (isClaimable(wo, meId)) pool.push(wo)
    else rest.push(wo)
  }

  mine.sort((a, b) => cardRank(a) - cardRank(b))
  rest.sort((a, b) => cardRank(a) - cardRank(b))
  return { mine, pool, rest, done }
}

/** What a service looked like last render. `mine` is part of it because
 *  "was this card already in your jobs?" can't be answered from state
 *  alone — a colleague's running service is `in_progress` too. */
export interface ServiceMemo {
  readonly state: DoListServiceState
  readonly mine: boolean
}

/**
 * The previous feed, reduced to what the animations need.
 *
 * `mineWoIds` is stored rather than re-derived from `services` because "was
 * this card mine?" is partly a fact about the JOB (dispatched to me, nothing
 * started), and a map of service states cannot answer it. Recomputing it
 * from the memos alone would silently drop every freshly dispatched card and
 * replay its entrance on the next poll.
 */
export interface TodaySnapshot {
  readonly services: ReadonlyMap<string, ServiceMemo>
  readonly mineWoIds: ReadonlySet<string>
}

export function serviceStateSnapshot(
  workOrders: readonly DoListWorkOrder[],
  meId: string | null = null,
): TodaySnapshot {
  const services = new Map<string, ServiceMemo>()
  const mineWoIds = new Set<string>()
  for (const wo of workOrders) {
    for (const service of wo.services) {
      services.set(service.id, { state: service.state, mine: service.mine })
    }
    if (isMine(wo, meId)) mineWoIds.add(wo.id)
  }
  return { services, mineWoIds }
}

export interface TodayTransitions {
  /** Services that just began running — play the green fill on these only. */
  readonly startedServiceIds: ReadonlySet<string>
  /** WOs that just entered "your jobs" — play the row entrance on these. */
  readonly arrivedWoIds: ReadonlySet<string>
}

export const NO_TRANSITIONS: TodayTransitions = {
  startedServiceIds: new Set(),
  arrivedWoIds: new Set(),
}

/**
 * Diff two snapshots into the things worth animating.
 *
 * With no previous feed to compare against — `prev` null, or empty because
 * the query hadn't resolved yet — nothing animates. A service that was
 * already running when the page opened didn't *start*, and replaying every
 * card's entrance on mount is noise, not feedback.
 */
export function diffTransitions(
  prev: TodaySnapshot | null,
  workOrders: readonly DoListWorkOrder[],
  meId: string | null = null,
): TodayTransitions {
  if (!prev || prev.services.size === 0) return NO_TRANSITIONS

  const startedServiceIds = new Set<string>()
  const arrivedWoIds = new Set<string>()

  for (const wo of workOrders) {
    for (const service of wo.services) {
      const before = prev.services.get(service.id)
      // An id absent from `prev` is new to the feed (Management just
      // created the job), not a state change — nothing transitioned.
      if (before === undefined || before.state === service.state) continue
      if (service.state === 'in_progress' && service.mine) startedServiceIds.add(service.id)
    }
    // Only a card that wasn't already mine has arrived. Starting a second
    // service on a card that's already up top must not replay its entrance,
    // and pausing keeps the card where it is.
    if (isMine(wo, meId) && !prev.mineWoIds.has(wo.id)) arrivedWoIds.add(wo.id)
  }

  return { startedServiceIds, arrivedWoIds }
}
